/* CustomFieldsRenderer.js */

/* global Slick */
"use strict";
const _ = require("lodash");

/**
 * @type {{Control:{Action:{Button:ButtonControlComponent}}}}
 * @const
 */
const uiCore = require("@gluenetworks/steve_ui_core");

const TypeInfoEditor = require("./cellEditors/TypeInfoEditor");
const DataTypeEditor = require("./cellEditors/DataTypeEditor");
const DefaultValueEditor = require("./cellEditors/DefaultValueEditor");
const TextAreaEditor = require("./cellEditors/TextAreaEditor");
const CheckBoxEditor = require("./cellEditors/CheckBoxEditor");
const InputTextEditor = require("./cellEditors/InputTextEditor");
const {DateEditor} = require("@gluenetworks/date_cell_editor");
const {parseLocalDate} = require("@gluenetworks/date_cell_editor").localDate;
const formatters = require("./cellFormaters");
const {withDialog} = require("./cellEditors/CommonEditor");

const ButtonControl = uiCore.Control.Action.Button;

const NO_DATA_PLACEHOLDER_MSG = "No Custom Fields Available";
const NO_TAGS_DATA_PLACEHOLDER_MSG = "No Tags Available";

const TAGS_INFO = "Expired tags are deleted at midnight UTC.";

const TABS = [
    {label: "Custom Fields", value: "customFields"},
    {label: "Tags", value: "tags"},
];

/**
 * class CustomFieldsRenderer
 *
 * @listens CustomFieldsComponent#event:DataUpdated
 * @listens CustomFieldsComponent#event:TagsDataUpdated
 *
 */
class CustomFieldsRenderer {
    /**
     *
     * @param {CustomFieldsComponent} model
     * @param {UiUtils} uiUtils
     */
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._canManage = model.canManage;
        this._rootElem = document.createElement("custom-fields-gl");
        this._rootElem.component = this._model;

        this._slickTableContainer = document.createElement("custom-fields-table");
        this._slickTableContainer.classList.toggle("read-only-grid", !this._canManage);
        this._slickWrapper = document.createElement("custom-fields-table-wrapper");
        this._slickWrapper.appendChild(this._slickTableContainer);

        this._slickTable = undefined;
        this._moveRowsPlugin = undefined;
        this._tabButtons = [];
        this._tabContents = [];
        this._activeTabType = TABS[0].value;

        this._tagsSlickTableContainer = document.createElement("custom-fields-table");
        this._tagsSlickTableContainer.classList.toggle("read-only-grid", !this._canManage);
        this._tagsSlickWrapper = document.createElement("custom-fields-table-wrapper");
        this._tagsSlickWrapper.classList.add("tags-table-wrapper");
        this._tagsSlickWrapper.appendChild(this._tagsSlickTableContainer);

        this._tagsSlickTable = undefined;

        const enableFieldEditor = withDialog(CheckBoxEditor, (item, state) => {
            const originalValue = _.get(this.model.data, [item.id, "enabled"], false);
            const isShared = _.get(item, "shared", false);
            if (originalValue === true && state === false) {
                return this._uiUtils.showDeleteConfirmDialog({
                    message: isShared
                        ? `Are you sure you want to disable the custom field "${item.name}"?
                            All data will be lost from this organization and any subordinate organizations.`
                        : `Are you sure you want to disable the custom field "${item.name}"?`,
                    confirmButtonName: "Confirm",
                    cancelButtonName: "Cancel",
                });
            }
            return Promise.resolve();
        });

        const SharedFieldEditor = withDialog(CheckBoxEditor, (item, state) => {
            const originalValue = _.get(this.model.data, [item.id, "shared"], false);
            if (originalValue === true && state === false) {
                return this._uiUtils.showDeleteConfirmDialog({
                    message: `Are you sure you want to stop sharing the custom field "${item.name}"?
                                    All data will be lost from any subordinate organizations.`,
                    confirmButtonName: "Confirm",
                    cancelButtonName: "Cancel",
                });
            }
            return Promise.resolve();
        });

        this._columns = [
            {
                id: "order",
                width: 30,
                name: "",
                //field: "order",
                behavior: "selectAndMove",
                asyncPostRender: function (node) {
                    node[0].setAttribute("title", "Reorder");
                },
                selectable: false,
                resizable: false,
                cssClass: "cell-reorder dnd centered",
            },

            {
                id: "enabled",
                width: 20,
                name: "Enabled",
                field: "enabled",
                toolTip: "Add this field in Device Details for every device in the organization",
                formatter: formatters.checkboxFormatter,
                editor: enableFieldEditor,
            },
            {
                id: "shared",
                width: 20,
                name: "Shared",
                field: "shared",
                toolTip: "Share field with child organizations",
                formatter: formatters.checkboxFormatter,
                editor: SharedFieldEditor,
            },
            {
                id: "name",
                width: 60,
                name: "Field Name",
                field: "name",
                toolTip: "Name the field",
                formatter: formatters.nameFormatter,
                editor: InputTextEditor,
            },
            {
                id: "dataType",
                width: 40,
                name: "Data Type",
                field: "dataType",
                toolTip: "Select the type of field",
                formatter: formatters.dataTypeFormatter,
                editor: DataTypeEditor,
            },
            {
                id: "typeInfo",
                width: 40,
                name: "Type Options",
                field: "typeInfo",
                toolTip: "Specify the parameters for the data type",
                formatter: formatters.typeInfoFormatter,
                editor: TypeInfoEditor,
            },
            {
                id: "default",
                width: 40,
                name: "Default Value",
                field: "default",
                toolTip: "Displayed default for this field",
                formatter: formatters.defaultValueFormatter,
                editor: DefaultValueEditor,
            },
            {
                id: "description",
                width: 100,
                name: "Description",
                field: "description",
                toolTip: "Displayed field description",
                formatter: formatters.descriptionFormatter,
                editor: TextAreaEditor,
            },
            {
                id: "deviceOnly",
                width: 35,
                name: "Device Only",
                field: "deviceOnly",
                toolTip: "Device only or site customfield",
                formatter: formatters.checkboxFormatter,
                editor: CheckBoxEditor,
                cssClass: "centered",
                headerCssClass: "centered",
            },
            {
                id: "groupable",
                width: 35,
                name: "Groupable",
                field: "groupable",
                toolTip:
                    "Add the custom field to the Group By option in the Device Explorer filter",
                formatter: formatters.checkboxFormatter,
                editor: CheckBoxEditor,
            },
            /*{
                id: "required",
                width: 40,
                name: "Required",
                field: "required",
                formatter: formatters.checkboxFormatter,
                editor: Slick.Editors.Checkbox
            },*/
            {
                id: "delete",
                width: 15,
                name: "Action",
                behavior: "deleteRow",
                selectable: false,
                toolTip: "Delete the custom field",
                formatter: formatters.deleteButtonFormatter,
                asyncPostRender: this._deleteCellAddTooltips.bind(this),
                cssClass: "cell-delete centered",
                headerCssClass: "centered",
            },
        ];

        // Snapshot before Slick can corrupt these - see _fitTableSize().
        this._columnWidths = this._columns.map((column) => column.width);

        this._tagsColumns = [
            {
                id: "name",
                width: 277,
                name: "Name",
                field: "name",
                toolTip: "Name the tag",
                formatter: formatters.nameFormatter,
                editor: InputTextEditor,
                headerCssClass: "left-aligned",
                sortable: true,
            },
            {
                id: "description",
                width: 419,
                name: "Description",
                field: "description",
                toolTip: "Displayed tag description",
                formatter: formatters.descriptionFormatter,
                editor: TextAreaEditor,
                headerCssClass: "left-aligned",
                sortable: true,
            },
            {
                id: "autoDeleteAt",
                width: 277,
                name: "Expiration",
                field: "autoDeleteAt",
                toolTip: "Date the tag will be automatically deleted",
                formatter: formatters.plainTextFormatter,
                editor: DateEditor,
                editorOptions: {minDate: "today", className: "settings-tags-date-editor"},
                headerCssClass: "left-aligned",
                sortable: true,
            },
            {
                id: "createdBy",
                width: 277,
                name: "Created by",
                field: "createdBy",
                toolTip: "User who created the tag",
                formatter: formatters.plainTextFormatter,
                headerCssClass: "left-aligned",
                cssClass: "readonly",
                sortable: true,
            },
            {
                id: "delete",
                width: 135,
                name: "Action",
                behavior: "deleteRow",
                selectable: false,
                toolTip: "Delete the tag",
                formatter: formatters.deleteButtonFormatter,
                asyncPostRender: this._deleteTagCellAddTooltips.bind(this),
                cssClass: "cell-delete centered",
                headerCssClass: "centered",
            },
        ];

        // Snapshot before Slick can corrupt these - see _fitTableSize().
        this._tagsColumnWidths = this._tagsColumns.map((column) => column.width);

        this._tableOptions = {
            editable: this._canManage,
            enableCellNavigation: true,
            enableAsyncPostRender: true,
            forceFitColumns: true,
            autoHeight: true,
            autoEdit: true,
            resizable: true,
            rowHeight: 45,
        };

        /**
         * @type {ButtonControlComponent}
         * @private
         */
        this._cancelButton = new ButtonControl({label: "Cancel", darkTheme: true});
        this._saveButton = new ButtonControl({label: "Save", active: true, darkTheme: true});
        this._addNewRowButton = new ButtonControl({
            label: "Add Custom Field +",
            active: true,
        });
        this._addNewRowWrapper = this._uiUtils.wrapElement(
            this._addNewRowButton.renderer.getRootElement(),
            null,
            "add-new-row"
        );

        this._controlButtonsContainer = document.createElement("div");
        this._controlButtonsContainer.classList.add("control-buttons");
        this._controlButtonsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._controlButtonsContainer.appendChild(this._saveButton.renderer.getRootElement());

        this._tagsCancelButton = new ButtonControl({label: "Cancel", darkTheme: true});
        this._tagsSaveButton = new ButtonControl({label: "Save", active: true, darkTheme: true});
        this._addNewTagRowButton = new ButtonControl({
            label: "Add Tag +",
            active: true,
        });
        this._addNewTagRowWrapper = this._uiUtils.wrapElement(
            this._addNewTagRowButton.renderer.getRootElement(),
            null,
            "add-new-row"
        );

        this._tagsControlButtonsContainer = document.createElement("div");
        this._tagsControlButtonsContainer.classList.add("control-buttons", "tags-control-buttons");
        this._tagsControlButtonsContainer.appendChild(
            this._tagsCancelButton.renderer.getRootElement()
        );
        this._tagsControlButtonsContainer.appendChild(
            this._tagsSaveButton.renderer.getRootElement()
        );

        this._clickHandler = this._clickHandler.bind(this);
        this._cancelAction = this._cancelAction.bind(this);
        this._fitTableSize = this._fitTableSize.bind(this);
        this._debouncedFitTableSize = _.debounce(this._fitTableSize, 150);
        this._addNewRowAction = this._addNewRowAction.bind(this);
        this._onTabClickEvent = this._onTabClickEvent.bind(this);

        this._onCellChangeAction = this._onCellChangeAction.bind(this);
        this._onRowCountChange = this._onRowCountChange.bind(this);
        this._onRowsChanged = this._onRowsChanged.bind(this);
        this._onBeforeEditCell = this._onBeforeEditCell.bind(this);

        this._cancelTagsAction = this._cancelTagsAction.bind(this);
        this._addNewTagRowAction = this._addNewTagRowAction.bind(this);
        this._tagsOnCellChangeAction = this._tagsOnCellChangeAction.bind(this);
        this._tagsOnRowCountChange = this._tagsOnRowCountChange.bind(this);
        this._tagsOnRowsChanged = this._tagsOnRowsChanged.bind(this);
        this._onTagsDataUpdated = this._onTagsDataUpdated.bind(this);
        this._saveTagsAction = this._saveTagsAction.bind(this);
        this._tagsOnSort = this._tagsOnSort.bind(this);

        this._tagsCollator = new Intl.Collator(undefined, {numeric: true, sensitivity: "base"});
    }

    get inheritedKeys() {
        return this.model.getFieldsKeys("inherited") || [];
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    shutdown() {
        this._unregisterListeners();
        this._uiUtils.clearMessage();
        if (this._slickTable && this._slickTable.destroy) {
            this._slickTable.destroy();
        }
        if (this._tagsSlickTable && this._tagsSlickTable.destroy) {
            this._tagsSlickTable.destroy();
        }
        this._rootElem.remove();

        return Promise.all([
            this._cancelButton.shutdown(),
            this._saveButton.shutdown(),
            this._addNewRowButton.shutdown(),
            this._tagsCancelButton.shutdown(),
            this._tagsSaveButton.shutdown(),
            this._addNewTagRowButton.shutdown(),
        ]).then(() => {
            this._model = null;
            this._slickTableContainer = null;
            this._moveRowsPlugin = null;
            this._tableOptions = null;
            this._columns = null;
            this._columnWidths = null;
            this._cancelButton = null;
            this._saveButton = null;
            this._addNewRowButton = null;
            this._addNewRowWrapper = null;
            this._controlButtonsContainer = null;
            this._slickTable = null;
            this._debouncedFitTableSize = null;
            this._tabButtons = null;
            this._tabContents = null;
            this._tabParent = null;

            this._tagsSlickTableContainer = null;
            this._tagsColumns = null;
            this._tagsColumnWidths = null;
            this._tagsCancelButton = null;
            this._tagsSaveButton = null;
            this._addNewTagRowButton = null;
            this._addNewTagRowWrapper = null;
            this._tagsControlButtonsContainer = null;
            this._tagsSlickTable = null;
            this._tagsCollator = null;

            this._rootElem = null;
        });
    }

    _registerListeners() {
        this._model.on("DataUpdated", (this._onDataUpdated = this._onDataUpdated.bind(this)));
        if (this._canManage) {
            this._cancelButton.on("UserClick", this._cancelAction);
            this._saveButton.on("UserClick", (this._saveAction = this._saveAction.bind(this)));
            this._addNewRowButton.on(
                "UserClick",
                (this._addNewRowAction = this._addNewRowAction.bind(this))
            );
        }
        //this._slickTable.onAddNewRow.subscribe(this._addNewRowAction.bind(this));
        window.addEventListener("resize", this._debouncedFitTableSize);
        document.addEventListener("click", this._clickHandler);
        if (this._slickTable) {
            this._slickTable.onCellChange.subscribe(this._onCellChangeAction);
            this._slickTable.onBeforeEditCell.subscribe(this._onBeforeEditCell);
            this._slickDataVIew.onRowCountChanged.subscribe(this._onRowCountChange);
            this._slickDataVIew.onRowsChanged.subscribe(this._onRowsChanged);
        }

        this._model.on("TagsDataUpdated", this._onTagsDataUpdated);
        if (this._canManage) {
            this._tagsCancelButton.on("UserClick", this._cancelTagsAction);
            this._tagsSaveButton.on("UserClick", this._saveTagsAction);
            this._addNewTagRowButton.on("UserClick", this._addNewTagRowAction);
        }
        if (this._tagsSlickTable) {
            this._tagsSlickTable.onCellChange.subscribe(this._tagsOnCellChangeAction);
            this._tagsSlickTable.onSort.subscribe(this._tagsOnSort);
            this._tagsSlickDataView.onRowCountChanged.subscribe(this._tagsOnRowCountChange);
            this._tagsSlickDataView.onRowsChanged.subscribe(this._tagsOnRowsChanged);
        }
    }

    _removeTabButtonListeners() {
        this._tabButtons.forEach((tabButton) =>
            tabButton.removeEventListener("click", this._onTabClickEvent)
        );
    }

    _unregisterListeners() {
        this._model.removeListener("DataUpdated", this._onDataUpdated);
        if (this._canManage) {
            this._cancelButton.removeListener("UserClick", this._cancelAction);
            this._saveButton.removeListener("UserClick", this._saveAction);
            this._addNewRowButton.removeListener("UserClick", this._addNewRowAction);
        }
        window.removeEventListener("resize", this._debouncedFitTableSize);
        this._debouncedFitTableSize.cancel();
        document.removeEventListener("click", this._clickHandler);
        this._removeTabButtonListeners();

        if (this._slickTable) {
            this._slickTable.onCellChange.unsubscribe(this._onCellChangeAction);
            this._slickTable.onBeforeEditCell.unsubscribe(this._onBeforeEditCell);
            this._slickDataVIew.onRowCountChanged.unsubscribe(this._onRowCountChange);
            this._slickDataVIew.onRowsChanged.unsubscribe(this._onRowsChanged);
        }

        this._model.removeListener("TagsDataUpdated", this._onTagsDataUpdated);
        if (this._canManage) {
            this._tagsCancelButton.removeListener("UserClick", this._cancelTagsAction);
            this._tagsSaveButton.removeListener("UserClick", this._saveTagsAction);
            this._addNewTagRowButton.removeListener("UserClick", this._addNewTagRowAction);
        }
        if (this._tagsSlickTable) {
            this._tagsSlickTable.onCellChange.unsubscribe(this._tagsOnCellChangeAction);
            this._tagsSlickTable.onSort.unsubscribe(this._tagsOnSort);
            this._tagsSlickDataView.onRowCountChanged.unsubscribe(this._tagsOnRowCountChange);
            this._tagsSlickDataView.onRowsChanged.unsubscribe(this._tagsOnRowsChanged);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => {
                    if (this._rootElem) {
                        this._rootElem.innerHTML = "";

                        const {customFieldsTabContent, tagsTabContent} = this._createTabs();

                        const customFieldsContent = document.createElement("div");
                        customFieldsContent.classList.add("box-content");
                        customFieldsContent.appendChild(this._slickWrapper);
                        if (this._canManage) {
                            customFieldsContent.appendChild(this._addNewRowWrapper);
                            customFieldsContent.appendChild(this._controlButtonsContainer);
                        }
                        customFieldsTabContent.appendChild(customFieldsContent);

                        this._setActionButtonsAvailability(false);
                        this._toggleNoDataPlaceholder(_.isEmpty(this._getModelData()));

                        // BEGIN data table preparation section
                        this._slickDataVIew = new Slick.Data.DataView();
                        this._slickDataVIew.setItems(this._getModelData());
                        this._sortTable();

                        this._slickDataVIew.getItemMetadata = (
                            (original_getItemMetadata) => (row) => {
                                const item = this._slickDataVIew.getItem(row);
                                let ret = original_getItemMetadata(row);
                                if (item.inherited) {
                                    ret = ret || {};
                                    ret.cssClasses =
                                        (ret.cssClasses || "") + " " + "inherited-field";
                                }
                                return ret;
                            }
                        )(this._slickDataVIew.getItemMetadata);

                        this._slickTable = new Slick.Grid(
                            this._slickTableContainer,
                            this._slickDataVIew,
                            this._columns,
                            this._tableOptions
                        );

                        this._slickTable.setSelectionModel(new Slick.RowSelectionModel());
                        if (this._canManage) {
                            this._registerRowMovePlugin(this._slickTable, this._slickDataVIew);
                        }

                        // END data table preparation section

                        this._slickTableContainer.appendChild(this._createNoDataPlaceholder());

                        const tagsContent = document.createElement("div");
                        tagsContent.classList.add("box-content");
                        tagsContent.appendChild(this._tagsSlickWrapper);
                        if (this._canManage) {
                            tagsContent.appendChild(this._addNewTagRowWrapper);
                        }

                        const tagsFooter = document.createElement("div");
                        tagsFooter.classList.add("tags-footer");
                        tagsFooter.appendChild(this._createTagsSummarySection());
                        if (this._canManage) {
                            tagsFooter.appendChild(this._tagsControlButtonsContainer);
                        }
                        tagsContent.appendChild(tagsFooter);
                        tagsTabContent.appendChild(tagsContent);

                        this._setTagsActionButtonsAvailability(false);
                        this._toggleTagsNoDataPlaceholder(_.isEmpty(this._getTagsModelData()));

                        this._tagsSlickDataView = new Slick.Data.DataView();
                        this._tagsSlickDataView.setItems(this._getTagsModelData());

                        this._tagsSlickTable = new Slick.Grid(
                            this._tagsSlickTableContainer,
                            this._tagsSlickDataView,
                            this._tagsColumns,
                            this._tableOptions
                        );
                        this._tagsSlickTable.setSelectionModel(new Slick.RowSelectionModel());
                        this._sortTagsByDefault();

                        this._tagsSlickTableContainer.appendChild(
                            this._createTagsNoDataPlaceholder()
                        );

                        this._registerListeners();
                    }
                    return Promise.resolve();
                })
                .catch((err) => console.error(err) || Promise.reject(err));
        }
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _addNewRowAction(/*e, args*/) {
        let item = Object.assign(this.model.createCustomFieldItem(), {complete: false});
        this._slickDataVIew.addItem(item);
        this._scrollWrapperToBottom(this._slickWrapper);

        this._setActionButtonsAvailability(true);
    }

    _addNewTagRowAction(/*e, args*/) {
        const item = Object.assign(this.model.createTagItem(), {complete: false});
        this._tagsSlickDataView.addItem(item);
        this._scrollWrapperToBottom(this._tagsSlickWrapper);

        this._setTagsActionButtonsAvailability(true);
    }

    // New rows are always appended last (neither add action re-sorts), so scrolling to the
    // wrapper's bottom always reveals the row just created. Deferred a frame since Slick's
    // autoHeight resize from addItem() isn't guaranteed to have painted synchronously yet.
    _scrollWrapperToBottom(wrapper) {
        requestAnimationFrame(() => {
            wrapper.scrollTo({top: wrapper.scrollHeight, behavior: "smooth"});
        });
    }

    /**
     * Commit and finish editing if click outside of the editor cell
     * @param {MouseEvent} e - click event
     * @param {SlickGrid} table - the grid whose active edit should be committed
     * @param {HTMLElement} container - the grid's table container element
     * @private
     */
    _saveAndCloseEditCellAction(
        e,
        table = this._slickTable,
        container = this._slickTableContainer
    ) {
        //do nothing if no active editors
        if (!table.getEditorLock().isActive()) {
            return false;
        }

        //get event bubbling path
        const path = e.path || e.composedPath();

        //check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === container) {
                return false;
            }
        });

        if (outsideClick) {
            table.getEditorLock().commitCurrentEdit();
        }
    }

    _deleteCellAddTooltips(cellNode /*, row, dataContext, colDef*/) {
        cellNode[0].setAttribute("title", "Delete Custom Field");
    }

    _deleteTagCellAddTooltips(cellNode /*, row, dataContext, colDef*/) {
        cellNode[0].setAttribute("title", "Delete Tag");
    }

    _setActionButtonsAvailability(state = null) {
        const currentData = this._slickDataVIew
            ? this._slickDataVIew.getItems()
            : this._getModelData();

        const isDisabled = state === null ? this._isDataNotBeenChanged(currentData) : !state;
        this._cancelButton.setDisabled(isDisabled);
        this._saveButton.setDisabled(isDisabled);
    }

    _setTagsActionButtonsAvailability(state = null) {
        const currentData = this._tagsSlickDataView
            ? this._tagsSlickDataView.getItems()
            : this._getTagsModelData();

        const isDisabled = state === null ? this._isTagsDataNotBeenChanged(currentData) : !state;
        this._tagsCancelButton.setDisabled(isDisabled);
        this._tagsSaveButton.setDisabled(isDisabled);
    }

    // Slick's forceFitColumns autosizing measures a hidden (display: none) container's
    // viewport as 0 and permanently overwrites that grid's column widths in place to their
    // minWidth floor. Resizing the inactive tab's grid would immediately re-corrupt it that
    // way, so only ever resize whichever grid is currently visible - restoring its configured
    // widths first undoes any earlier corruption before autosizeColumns runs again for real.
    _fitTableSize() {
        if (this._activeTabType === "tags") {
            if (this._tagsSlickTable) {
                this._restoreColumnWidths(this._tagsColumns, this._tagsColumnWidths);
                this._tagsSlickTable.resizeCanvas();
            }
        } else if (this._slickTable) {
            this._restoreColumnWidths(this._columns, this._columnWidths);
            this._slickTable.resizeCanvas();
        }
    }

    _restoreColumnWidths(columns, widths) {
        columns.forEach((column, i) => {
            column.width = widths[i];
        });
    }

    _sortTable(sortByField) {
        sortByField = sortByField || "order";
        if (this._slickDataVIew) {
            this._slickDataVIew.sort(
                this._model.CustomFieldsEntity.getSortingComparator(sortByField)
            );
        }
    }

    _showError(error) {
        if (_.get(error, "errorCode") !== "NoPendingUpdates") {
            this._uiUtils.showMessageDialog({
                message: "An error occurred: " + this._uiUtils.errorCodeMapping(error),
                confirmButtonName: "OK",
            });
        }
    }

    _createNoDataPlaceholder() {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = NO_DATA_PLACEHOLDER_MSG;
        return noDataPlaceholder;
    }

    _createTagsNoDataPlaceholder() {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = NO_TAGS_DATA_PLACEHOLDER_MSG;
        return noDataPlaceholder;
    }

    _createTagsSummarySection() {
        const section = document.createElement("section");
        section.classList.add("summary");
        const tagsInfo = document.createElement("p");
        tagsInfo.innerHTML = TAGS_INFO;
        section.appendChild(tagsInfo);
        return section;
    }

    /**
     * Builds the Custom Fields / Tags tab strip and empty tab-content panels, appended to
     * _rootElem. Both panels stay mounted for the component's lifetime (only their "show"
     * class toggles) so each grid's independent dirty state survives switching tabs.
     * @returns {{customFieldsTabContent: HTMLElement, tagsTabContent: HTMLElement}}
     */
    _createTabs() {
        this._tabParent = document.createElement("div");
        this._tabParent.classList.add("tabs");
        this._rootElem.appendChild(this._tabParent);

        // reset tabs and tab buttons on re-render
        this._removeTabButtonListeners();
        this._tabButtons = [];
        this._tabContents = [];
        this._activeTabType = TABS[0].value;

        const tabContentByValue = {};
        TABS.forEach((tab) => {
            const isDefault = tab.value === TABS[0].value;

            const tabButton = document.createElement("button");
            tabButton.classList.add("tab-btn");
            tabButton.classList.toggle("selected", isDefault);
            tabButton.innerText = tab.label;
            tabButton.setAttribute("data-type", tab.value);
            tabButton.addEventListener("click", this._onTabClickEvent);
            this._tabButtons.push(tabButton);
            this._tabParent.appendChild(tabButton);

            const tabContent = document.createElement("div");
            tabContent.classList.add("tab-content");
            tabContent.classList.toggle("show", isDefault);
            tabContent.setAttribute("data-type", tab.value);
            this._tabContents.push(tabContent);
            this._rootElem.appendChild(tabContent);

            tabContentByValue[tab.value] = tabContent;
        });

        return {
            customFieldsTabContent: tabContentByValue.customFields,
            tagsTabContent: tabContentByValue.tags,
        };
    }

    _onTabClickEvent(event) {
        const selectedType = event.currentTarget.getAttribute("data-type");

        this._tabButtons.forEach((tabButton) => {
            tabButton.classList.toggle(
                "selected",
                tabButton.getAttribute("data-type") === selectedType
            );
        });

        this._tabContents.forEach((tabContent) => {
            tabContent.classList.toggle(
                "show",
                tabContent.getAttribute("data-type") === selectedType
            );
        });

        this._activeTabType = selectedType;

        // Grids built or resized while their tab was hidden (display: none) size themselves
        // at 0 - resize the newly-visible one now that it has a real viewport width.
        this._fitTableSize();
    }

    _toggleNoDataPlaceholder(state) {
        this._slickTableContainer.classList.toggle("empty", state);
    }

    _toggleTagsNoDataPlaceholder(state) {
        this._tagsSlickTableContainer.classList.toggle("empty", state);
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onCellChangeAction() {
        this._setActionButtonsAvailability();
    }

    _tagsOnCellChangeAction() {
        this._setTagsActionButtonsAvailability();
    }

    // Marks the header and reorders rows the same way a real click on Name would.
    _sortTagsByDefault() {
        const nameColumn = this._tagsColumns.find((column) => column.field === "name");
        this._tagsSlickTable.setSortColumn(nameColumn.id, true);
        this._tagsOnSort(null, {sortCol: nameColumn, sortAsc: true});
    }

    // Blanks compare as least, so they land first on ascending / last on descending.
    _tagsOnSort(e, {sortCol, sortAsc}) {
        const field = sortCol.field;
        const compareNonBlank =
            field === "autoDeleteAt"
                ? (value1, value2) =>
                      parseLocalDate(value1).getTime() - parseLocalDate(value2).getTime()
                : (value1, value2) => this._tagsCollator.compare(value1, value2);

        this._tagsSlickDataView.sort((row1, row2) => {
            const value1 = row1[field];
            const value2 = row2[field];
            const blank1 = !value1 || !String(value1).trim();
            const blank2 = !value2 || !String(value2).trim();
            if (blank1 && blank2) {
                return 0;
            } else if (blank1) {
                return -1;
            } else if (blank2) {
                return 1;
            }
            return compareNonBlank(value1, value2);
        }, sortAsc);

        this._tagsSlickTable.resizeCanvas();
        this._tagsSlickTable.invalidate();
        this._tagsSlickTable.render();
    }

    _onRowCountChange(event, {/*previous,*/ current}) {
        this._slickTable.invalidateAllRows();
        this._slickTable.resizeCanvas();
        this._toggleNoDataPlaceholder(!current);
    }

    _tagsOnRowCountChange(event, {/*previous,*/ current}) {
        this._tagsSlickTable.invalidateAllRows();
        this._tagsSlickTable.resizeCanvas();
        this._toggleTagsNoDataPlaceholder(!current);
    }

    _onRowsChanged() {
        this._setActionButtonsAvailability();
    }

    _tagsOnRowsChanged() {
        this._setTagsActionButtonsAvailability();
    }

    _onBeforeEditCell(e, {item, column /*, row, cell, grid*/}) {
        //allow to edit according to the following conditions:
        const allowConditions = [
            !this.inheritedKeys.includes(item.id), //do not allow if inherited field
            !(column.field === "dataType" && !item.isNew), //do not allow change dataType if item is not new
        ];

        return allowConditions.every(Boolean);
    }

    _onDataUpdated() {
        this._resetData();
    }

    _onTagsDataUpdated() {
        this._resetTagsData();
    }

    _clickHandler(e) {
        //if we click delete column
        const target = e.target;
        if (
            this._canManage &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete"
        ) {
            if (target.dataset.uuid && this._slickDataVIew.getItemById(target.dataset.uuid)) {
                this._deleteItem(target.dataset.uuid);
            } else if (
                this._tagsSlickDataView &&
                target.dataset.uuid &&
                this._tagsSlickDataView.getItemById(target.dataset.uuid)
            ) {
                this._deleteTagItem(target.dataset.uuid);
            }
        }
        this._saveAndCloseEditCellAction(e, this._slickTable, this._slickTableContainer);
        if (this._tagsSlickTable) {
            this._saveAndCloseEditCellAction(
                e,
                this._tagsSlickTable,
                this._tagsSlickTableContainer
            );
        }
    }

    _cancelAction() {
        this._resetData();
    }

    _cancelTagsAction() {
        this._resetTagsData();
    }

    _saveAction(e) {
        this._setActionButtonsAvailability(false);

        this._uiUtils.clearMessage();
        this._saveAndCloseEditCellAction(e, this._slickTable, this._slickTableContainer);
        const data = this._slickDataVIew.getItems();
        //exclude inherited Custom fields
        const userEditableData = _.reject(data, (item) => this.inheritedKeys.includes(item.id));

        const convertedData = this._convertSlickTableFormatToCustomFieldsData(userEditableData);
        this.model
            .updateFields(convertedData)
            .then(() => this._resetData())
            .catch((error) => {
                if (_.isArray(error)) {
                    this._uiUtils.writeErrorMessage(`Error: ${error.join("; ")}`);
                    // grid still has the unsaved edit - keep Save enabled to retry
                    this._saveButton.setDisabled(false);
                } else if (_.has(error, "errorCode")) {
                    this._resetData();
                    this._showError(error);
                } else {
                    console.error(error);
                }
                this._cancelButton.setDisabled(false);
            });
    }

    _saveTagsAction(e) {
        this._setTagsActionButtonsAvailability(false);

        this._uiUtils.clearMessage();
        this._saveAndCloseEditCellAction(e, this._tagsSlickTable, this._tagsSlickTableContainer);
        const data = this._tagsSlickDataView.getItems();

        this.model
            .updateTags(data)
            .then(() => this._resetTagsData())
            .catch((error) => {
                if (_.isArray(error)) {
                    this._uiUtils.writeErrorMessage(`Error: ${error.join("; ")}`);
                    // grid still has the unsaved edit - keep Save enabled to retry
                    this._tagsSaveButton.setDisabled(false);
                } else if (_.has(error, "errorCode")) {
                    this._resetTagsData();
                    this._showError(error);
                } else {
                    console.error(error);
                }
                this._tagsCancelButton.setDisabled(false);
            });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /**
     * Map function to convert CustomField item data to SlickGrid item data
     * @param data
     * @returns {function(Object=): Object}
     * @private
     */
    _convertCustomFieldsDataToSlickTableFormat(data) {
        return (key) =>
            Object.assign({}, data[key], {
                id: key,
                inherited: this.inheritedKeys.includes(key),
            });
    }

    /**
     * Map function to convert a loaded TagsEntity instance to a SlickGrid item
     * @param data
     * @returns {function(Object=): Object}
     * @private
     */
    _convertTagsDataToSlickTableFormat(data) {
        return (key) => {
            const entity = data[key];
            return {
                id: key,
                name: entity.name,
                description: entity.description,
                autoDeleteAt: entity.autoDeleteAt
                    ? new Date(entity.autoDeleteAt).toLocaleDateString()
                    : "",
                createdBy: entity.createdBy,
            };
        };
    }

    /**
     * Convert SlickGrid data to proper format before saving
     * @param {Array<Object>} slickGridData - pure SlickGrid data
     * @returns {Object<string, CustomFieldData>} - converted data object
     */
    _convertSlickTableFormatToCustomFieldsData(slickGridData) {
        return slickGridData.reduce((accumulator, customField, idx) => {
            const fieldId = customField.id;
            accumulator[fieldId] = {
                name: customField.name,
                dataType: customField.dataType,
                typeInfo: customField.typeInfo,
                default: customField.default,
                description: customField.description,
                enabled: !!customField.enabled,
                groupable: !!customField.groupable,
                required: !!customField.required,
                shared: !!customField.shared,
                deviceOnly: !!customField.deviceOnly,
                order: idx,
            };
            return accumulator;
        }, {});
    }

    _resetData() {
        const data = this._getModelData();
        this._slickDataVIew.setItems(data);
        this._sortTable();
        this._slickTable.invalidate();

        this._setActionButtonsAvailability(false);
    }

    _resetTagsData() {
        const data = this._getTagsModelData();
        this._tagsSlickDataView.setItems(data);
        this._sortTagsByDefault();
        this._tagsSlickTable.invalidate();

        this._setTagsActionButtonsAvailability(false);
    }

    _isDataNotBeenChanged(currentData) {
        return _.isEqual(this._getModelData(), currentData);
    }

    _isTagsDataNotBeenChanged(currentData) {
        return _.isEqual(this._getTagsModelData(), currentData);
    }

    _getModelData() {
        const modelKeys = this.model.keys;
        const data = this.model.data;
        const keys = []
            .concat(modelKeys.inherited, modelKeys.editable)
            .filter((key, idx, list) => idx === list.indexOf(key));
        return keys.map(this._convertCustomFieldsDataToSlickTableFormat(data));
    }

    _getTagsModelData() {
        const data = this.model.tagsData;
        return Object.keys(data).map(this._convertTagsDataToSlickTableFormat(data));
    }

    _deleteItem(uuid) {
        const item = this._slickDataVIew.getItemById(uuid);

        const deleteConfirmation = (item) => {
            const deletedItemName = _.get(item, "name", "Unknown");
            const deleteMessage =
                _.get(item, "shared", false) === true
                    ? `Are you sure you want to permanently delete the custom field "${deletedItemName}"?
                   All data will be lost from this organization and any subordinate organizations.`
                    : `Are you sure you want to permanently delete the custom field "${deletedItemName}"?`;

            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };

        return deleteConfirmation(item)
            .then(() => this.model.deleteField(uuid))
            .then(() => {
                this._slickDataVIew.deleteItem(uuid);
                this._setActionButtonsAvailability();
            })
            .catch((error) => error && this._showError(error));
    }

    _deleteTagItem(uuid) {
        const item = this._tagsSlickDataView.getItemById(uuid);
        const deletedItemName = _.get(item, "name", "Unknown");

        return this._uiUtils
            .showDeleteConfirmDialog({
                message: `Are you sure you want to permanently delete the tag "${deletedItemName}"?`,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            })
            .then(() => this.model.deleteTag(uuid))
            .then(() => {
                this._tagsSlickDataView.deleteItem(uuid);
                this._setTagsActionButtonsAvailability();
            })
            .catch((error) => error && this._showError(error));
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/

    _registerRowMovePlugin(grid, dataView) {
        const moveRowsPlugin = new Slick.RowMoveManager({
            cancelEditOnDrag: true,
        });
        const sortingComparator = this._model.CustomFieldsEntity.getSortingComparator("order");

        moveRowsPlugin.onBeforeMoveRows.subscribe(function (e, {insertBefore, rows}) {
            rows = [...rows].sort();
            if (
                _.first(rows) === insertBefore ||
                _.last(rows) === insertBefore - 1 ||
                _.get(dataView.getItem(insertBefore), "inherited")
            ) {
                e.stopPropagation();
                return false;
            }
            return true;
        });
        moveRowsPlugin.onMoveRows.subscribe(function (e, {insertBefore, rows}) {
            rows = [...rows].sort();
            const moveForward = _.last(rows) < insertBefore;
            const moveBackward = _.first(rows) > insertBefore;
            const delta = moveForward
                ? insertBefore - 1 - _.last(rows)
                : -(_.last(rows) - insertBefore);

            const currentData = dataView.getItems().map((item, idx) => {
                let order;
                if (
                    (moveBackward && (idx < insertBefore || idx > _.last(rows))) ||
                    (moveForward && (idx < _.first(rows) || idx >= insertBefore))
                ) {
                    //items are out of update range
                    order = idx;
                } else {
                    if (rows.includes(idx)) {
                        order = idx + delta;
                    } else {
                        order = idx + rows.length * (moveForward ? -1 : 1);
                    }
                }
                return Object.assign({}, item, {order});
            });

            dataView.beginUpdate();
            currentData.forEach((item) => dataView.updateItem(item.id, item));
            dataView.sort(sortingComparator);
            dataView.endUpdate();
            grid.invalidate();
        });

        grid.registerPlugin(moveRowsPlugin);
    }
}

module.exports = CustomFieldsRenderer;
